import React from "react";

const EmployeeList = ({
  employees,
  organizations,
  roles = [],
  setSelectedEmployee,
  setActiveView,
  deleteEmployee,
}) => {
  const handleEdit = (emp) => {
    setSelectedEmployee(emp);
    setActiveView("add-employee");
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      deleteEmployee(id);
    }
  };

  const getOrganizationName = (orgId) => {
    const org = organizations?.find((o) => o.id === orgId);
    return org ? org.name : "Unknown Organization";
  };

  const getRoleInfo = (roleId, position) => {
    if (roleId && roles && Array.isArray(roles)) {
      const role = roles.find((r) => r.id === roleId);
      if (role) {
        return {
          name: role.name,
          department: role.department,
          level: role.level,
          hasRole: true,
        };
      }
    }
    return {
      name: position || "No Role",
      hasRole: false,
    };
  };

  const handleViewRoleDetails = (roleId) => {
    if (roleId && roles && Array.isArray(roles)) {
      const role = roles.find((r) => r.id === roleId);
      if (role) {
        alert(
          `Role Details:\n\n` +
            `Name: ${role.name}\n` +
            `Department: ${role.department}\n` +
            `Level: ${role.level}\n` +
            `Description: ${role.description || "N/A"}`,
        );
      }
    }
  };

  // Check if roles are loaded
  if (!roles || !Array.isArray(roles)) {
    return (
      <div className="employees-view">
        <div className="list-header">
          <h2>
            <span>👥</span> Employees
          </h2>
        </div>
        <div
          className="loading-message"
          style={{ textAlign: "center", padding: "3rem" }}
        >
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employees-view">
      <div className="list-header">
        <h2>
          <span>👥</span> Employees
        </h2>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <button
            className="add-btn"
            onClick={() => {
              setSelectedEmployee(null);
              setActiveView("add-employee");
            }}
          >
            <span>➕</span> Add New Employee
          </button>
          <button
            className="add-btn"
            style={{ backgroundColor: "#9b59b6" }}
            onClick={() => setActiveView("roles")}
          >
            <span>👔</span> Manage Roles
          </button>
        </div>
      </div>

      {employees && employees.length > 0 ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Organization</th>
                <th>Role</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Hire Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const roleInfo = getRoleInfo(emp.roleId, emp.position);

                return (
                  <tr key={emp.id}>
                    <td>{emp.id}</td>
                    <td>{emp.firstName}</td>
                    <td>{emp.lastName}</td>
                    <td>{getOrganizationName(emp.organizationId)}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: roleInfo.hasRole
                              ? "#3498db"
                              : "#95a5a6",
                            color: "white",
                            padding: "0.3rem 0.6rem",
                            borderRadius: "4px",
                            fontSize: "0.9rem",
                            cursor: roleInfo.hasRole ? "pointer" : "default",
                          }}
                          onClick={() =>
                            roleInfo.hasRole &&
                            handleViewRoleDetails(emp.roleId)
                          }
                          title={
                            roleInfo.hasRole ? "Click to view role details" : ""
                          }
                        >
                          {roleInfo.name}
                        </span>
                        {roleInfo.hasRole && roleInfo.department && (
                          <span
                            style={{
                              backgroundColor: getDepartmentColor(
                                roleInfo.department,
                              ),
                              color: "white",
                              padding: "0.2rem 0.5rem",
                              borderRadius: "12px",
                              fontSize: "0.8rem",
                            }}
                          >
                            {roleInfo.department}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{emp.email}</td>
                    <td>{emp.phone}</td>
                    <td>{emp.hireDate}</td>
                    <td className="action-cell">
                      <button
                        className="icon-btn edit-btn"
                        onClick={() => handleEdit(emp)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="icon-btn delete-btn"
                        onClick={() => handleDelete(emp.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                      {emp.roleId && (
                        <button
                          className="icon-btn"
                          style={{ color: "#9b59b6" }}
                          onClick={() => handleViewRoleDetails(emp.roleId)}
                          title="View Role Details"
                        >
                          👁️
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-message">
          <p>No employees yet. Add the first employee.</p>
          <button
            className="add-btn"
            style={{ marginTop: "1rem" }}
            onClick={() => setActiveView("add-employee")}
          >
            <span>➕</span> Add Employee
          </button>
        </div>
      )}

      {/* Role Stats */}
      <div
        className="stats-section"
        style={{
          marginTop: "2rem",
          backgroundColor: "white",
          borderRadius: "10px",
          padding: "1.5rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <h3 style={{ marginBottom: "1rem", color: "#2c3e50" }}>
          <span>📊</span> Role Statistics
        </h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              minWidth: "200px",
            }}
          >
            <h4 style={{ marginBottom: "0.5rem", color: "#7f8c8d" }}>Total</h4>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "#3498db",
                  }}
                >
                  {employees?.filter((emp) => emp.roleId).length || 0}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#95a5a6" }}>
                  employees with role
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "#95a5a6",
                  }}
                >
                  {employees?.filter((emp) => !emp.roleId).length || 0}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#95a5a6" }}>
                  without role
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "1rem",
              borderRadius: "8px",
              flex: "2",
              minWidth: "300px",
            }}
          >
            <h4 style={{ marginBottom: "0.5rem", color: "#7f8c8d" }}>
              Most Common Roles
            </h4>
            <div style={{ maxHeight: "150px", overflowY: "auto" }}>
              {(() => {
                if (!employees || !roles) {
                  return (
                    <p style={{ color: "#95a5a6", textAlign: "center" }}>
                      No data
                    </p>
                  );
                }

                const roleCounts = {};
                employees.forEach((emp) => {
                  if (emp.roleId) {
                    const role = roles.find((r) => r.id === emp.roleId);
                    if (role) {
                      roleCounts[role.name] = (roleCounts[role.name] || 0) + 1;
                    }
                  }
                });

                const sortedRoles = Object.entries(roleCounts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5);

                if (sortedRoles.length === 0) {
                  return (
                    <p style={{ color: "#95a5a6", textAlign: "center" }}>
                      No data
                    </p>
                  );
                }

                return sortedRoles.map(([roleName, count]) => (
                  <div
                    key={roleName}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.5rem 0",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <span>{roleName}</span>
                    <span
                      style={{
                        backgroundColor: "#3498db",
                        color: "white",
                        padding: "0.2rem 0.6rem",
                        borderRadius: "12px",
                        fontSize: "0.9rem",
                      }}
                    >
                      {count} employees
                    </span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function for department colors
const getDepartmentColor = (department) => {
  const colors = {
    IT: "#3498db",
    Management: "#2ecc71",
    Marketing: "#9b59b6",
    Sales: "#e67e22",
    Finance: "#e74c3c",
    "Human Resources": "#1abc9c",
    Security: "#f39c12",
    Other: "#95a5a6",
  };
  return colors[department] || "#95a5a6";
};

export default EmployeeList;
